import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";
import { Container, Table } from "reactstrap";

import { Heading } from "components/Heading";
import { CustomModal } from "components/CustomModal";
import { CustomButton } from "components/CustomButton";
import { PageHeadings, PageTitles } from "../../constants";
import {
  licensePaymentsApi,
  licensePaymentsNeedingAttention,
  refundLicensePayment,
} from "../../api/api.service";

/**
 * Every payment on the platform.
 *
 * There was no way to see one before this. Support could read the plan catalogue
 * and every school's licence but not a single transaction, so "did this teacher
 * actually pay" could only be answered by opening the gateway's own dashboard
 * and matching by eye.
 *
 * The banner at the top is the point of the page as much as the table is. A
 * payment sitting unresolved means we asked a gateway for money and do not yet
 * know what happened — the customer may have been charged and be locked out
 * right now. Putting that count on a screen somebody already opens is what makes
 * "nobody found out" stop being reachable.
 *
 * Paginated by cursor rather than page number, because OFFSET re-reads and
 * discards every skipped row and this table only grows.
 */

const STATUSES = ["", "SUCCEEDED", "PENDING", "PENDING_CONFIRMATION", "FAILED", "EXPIRED", "REFUNDED"];

const money = (cents, currency) => {
  const symbol = currency === "USD" ? "$" : `${currency} `;
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  // Formatted from integer cents, never through a float: 9.99 has no exact
  // binary representation and an invoice that reads $9.98 is a support ticket.
  return `${sign}${symbol}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
};

const day = (iso) => (iso ? String(iso).slice(0, 10) : "—");

const whoFor = (row) => {
  if (row.buyer) return row.buyer.email || row.buyer.name || `User ${row.buyer.id}`;
  if (row.school) return `School ${row.school.schoolId ?? "—"} (${row.school.tier})`;
  return "—";
};

export default function LicensePayments() {
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [status, setStatus] = useState("");
  const [needsAttention, setNeedsAttention] = useState(false);
  const [attentionCount, setAttentionCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (cursor) params.cursor = cursor;
      if (status) params.status = status;
      if (needsAttention) params.needsAttention = "true";

      const res = await licensePaymentsApi(params);
      // Appending rather than replacing when paging forward: the cursor only
      // moves one way, so a "load more" that replaced the table would lose
      // everything above it.
      setRows((prev) => (cursor ? [...prev, ...(res?.data ?? [])] : res?.data ?? []));
      setNextCursor(res?.nextCursor ?? null);
    } catch (err) {
      toast.error(err?.message || "Could not load payments.");
    } finally {
      setLoading(false);
    }
  }, [cursor, status, needsAttention]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    licensePaymentsNeedingAttention()
      .then((res) => setAttentionCount(res?.data?.count ?? 0))
      .catch(() => setAttentionCount(0));
  }, [rows]);

  const applyFilter = (next) => {
    // Any filter change restarts paging. Keeping the cursor would page through
    // a set the cursor was never taken from.
    setCursor(null);
    next();
  };

  const doRefund = async () => {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      await refundLicensePayment(refundTarget.id, {});
      toast.success(`Refunded ${money(refundTarget.totalCents, refundTarget.currency)}.`);
      setRefundTarget(null);
      setCursor(null);
      await load();
    } catch (err) {
      // Left open on failure, with the reason. A modal that closes on an error
      // reads as success.
      toast.error(err?.message || "The refund did not go through.");
    } finally {
      setRefunding(false);
    }
  };

  return (
    <Container fluid>
      <Helmet>
        <title>{PageTitles.LICENSE_PAYMENTS}</title>
      </Helmet>

      <Heading title={PageHeadings.LICENSE_PAYMENTS} />

      {attentionCount > 0 ? (
        <div
          role="status"
          className="mb-3 p-3"
          style={{ background: "#f7eae6", border: "1px solid #e0b3a3", borderRadius: 10 }}
        >
          <strong>{attentionCount}</strong>{" "}
          {attentionCount === 1 ? "payment is" : "payments are"} unresolved. We asked the gateway for
          money and do not yet know what happened — someone may have been charged and be locked out.
          <button
            type="button"
            className="btn btn-link p-0 ms-2"
            onClick={() => applyFilter(() => setNeedsAttention(true))}
          >
            Show them
          </button>
        </div>
      ) : null}

      <div className="d-flex align-items-center gap-3 mb-3">
        <label htmlFor="payment-status" className="mb-0">
          Status
        </label>
        <select
          id="payment-status"
          value={status}
          onChange={(e) => applyFilter(() => setStatus(e.target.value))}
          style={{ minHeight: 38, minWidth: 200 }}
        >
          {STATUSES.map((s) => (
            <option key={s || "all"} value={s}>
              {s || "All"}
            </option>
          ))}
        </select>

        <label className="mb-0">
          <input
            type="checkbox"
            checked={needsAttention}
            onChange={(e) => applyFilter(() => setNeedsAttention(e.target.checked))}
          />{" "}
          Unresolved only
        </label>
      </div>

      <Table responsive>
        <thead>
          <tr>
            <th>Invoice</th>
            <th>Who for</th>
            <th>Period</th>
            <th className="text-end">Amount</th>
            <th>Status</th>
            <th>Paid</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{row.invoiceNumber || `#${row.id}`}</td>
              <td>{whoFor(row)}</td>
              <td>
                {day(row.periodStart)} → {day(row.periodEnd)}
              </td>
              <td className="text-end">{money(row.totalCents, row.currency)}</td>
              <td>
                {row.status}
                {row.refundsPaymentId ? ` (refund of #${row.refundsPaymentId})` : ""}
                {row.failureReason ? <div className="text-muted small">{row.failureReason}</div> : null}
              </td>
              <td>{day(row.paidAt)}</td>
              <td>
                {/* Refundable only when it is a real, settled charge. A refund
                    of a refund creates a charge nobody made. */}
                {row.status === "SUCCEEDED" && !row.refundsPaymentId && row.totalCents > 0 ? (
                  <CustomButton size="sm" color="link" onClick={() => setRefundTarget(row)} title="Refund" />
                ) : null}
              </td>
            </tr>
          ))}
          {!rows.length && !loading ? (
            <tr>
              <td colSpan={7} className="text-center text-muted py-4">
                No payments match this filter.
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>

      {nextCursor ? (
        <CustomButton
          title={loading ? "Loading…" : "Load more"}
          disabled={loading}
          onClick={() => setCursor(nextCursor)}
        />
      ) : null}

      <CustomModal isOpen={!!refundTarget} toggle={() => !refunding && setRefundTarget(null)}>
        {refundTarget ? (
          <div className="p-3">
            <h5>Refund this payment?</h5>
            <p className="mb-2">
              {money(refundTarget.totalCents, refundTarget.currency)} to {whoFor(refundTarget)}.
            </p>
            {/* Said explicitly. The money half looks like the whole job, and the
                access half is the one that gets forgotten. */}
            <p className="text-muted small">
              This refunds the money at the gateway and removes the access it paid for. It cannot be
              undone from here.
            </p>
            <div className="d-flex gap-2 mt-3">
              <CustomButton
                title={refunding ? "Refunding…" : "Refund"}
                disabled={refunding}
                onClick={doRefund}
              />
              <CustomButton
                title="Cancel"
                color="secondary"
                disabled={refunding}
                onClick={() => setRefundTarget(null)}
              />
            </div>
          </div>
        ) : null}
      </CustomModal>
    </Container>
  );
}
